import {
    StrongoShipmentParcel,
    type StrongoShipmentParcelData
} from "@/types/shipments/Strongo/StrongoShipmentParcel.ts";
import type {Company} from "@/types/company/company.ts";
import {StrongoTransportOrder, type StrongoTransportOrderData} from "@/types/order-entry/StrongoTransportOrder.ts";

export class StrongoShipment {
    public id: number;
    public company_id: number;
    public client_code: string;
    public no: string;
    public from_time: Date | null;
    public scheduled_pickup_date: Date | null;
    public pickup_date: Date | null;
    public to_time: Date | null;
    public scheduled_delivery_date: Date | null;
    public last_status_date: Date | null;
    public status: string;
    public substatus: string;
    public color: string;
    public text_color: string;
    public description: string;
    public from_name: string;
    public from_city: string;
    public from_county: string;
    public from_address: string;
    public to_name: string;
    public to_city: string;
    public to_county: string;
    public to_address: string;
    public hub: string;
    public type: string;
    public weight: number;
    public to_phone: number;
    public from_phone: number;
    public width: number;
    public length: number;
    public height: number;
    public comments: string;
    public to_zipcode: string;
    public from_zipcode: string;
    public customer_reference: string;
    public unload_delay_kpi: string | null;
    public department_name: string | null;
    public has_labels: boolean;
    public strongoShipmentParcels: StrongoShipmentParcel[];
    public company: Company | null;
    public transportOrder: StrongoTransportOrder | null;

    constructor(data: StrongoShipmentData | void) {
        if(data === undefined) {
            this.id = 0;
            this.company_id = 0;
            this.client_code = "";
            this.no = "";
            this.from_time = null;
            this.scheduled_pickup_date = null;
            this.pickup_date = null;
            this.to_time = null;
            this.scheduled_delivery_date = null;
            this.last_status_date = null;
            this.status = "";
            this.substatus = "";
            this.color = "";
            this.text_color = "";
            this.description = "";
            this.from_name = "";
            this.from_city = "";
            this.from_county = "";
            this.from_address = "";
            this.to_name = "";
            this.to_city = "";
            this.to_county = "";
            this.to_address = "";
            this.hub = "";
            this.type = "";
            this.weight = 0;
            this.to_phone = 0;
            this.from_phone = 0;
            this.width = 0;
            this.length = 0;
            this.height = 0;
            this.comments = '';
            this.to_zipcode = '';
            this.from_zipcode = '';
            this.customer_reference = "";
            this.unload_delay_kpi = null;
            this.department_name = null;
            this.has_labels = false;
            this.strongoShipmentParcels = [];
            this.company = null;
            this.transportOrder = null;

        } else {
            this.id = data.id;
            this.company_id = data.company_id;
            this.client_code = data.client_code;
            this.no = data.no;
            this.from_time = null;
            if (data.from_time !== null) {
                this.from_time = new Date(data.from_time);
            }
            this.scheduled_pickup_date = null;
            if (data.scheduled_pickup_date !== null) {
                this.scheduled_pickup_date = new Date(data.scheduled_pickup_date);
            }
            this.pickup_date = null;
            if (data.pickup_date !== null) {
                this.pickup_date = new Date(data.pickup_date);
            }
            this.to_time = null;
            if (data.to_time !== null) {
                this.to_time = new Date(data.to_time);
            }
            this.scheduled_delivery_date = null;
            if (data.scheduled_delivery_date !== null) {
                this.scheduled_delivery_date = new Date(data.scheduled_delivery_date);
            }
            this.last_status_date = null;
            if (data.last_status_date !== null) {
                this.last_status_date = new Date(data.last_status_date);
            }
            this.status = data.status;
            this.substatus = data.substatus;
            this.color = data.color;
            this.text_color = data.text_color;
            this.description = data.description;
            this.from_name = data.from_name;
            this.from_city = data.from_city;
            this.from_county = data.from_county;
            this.from_address = data.from_address;
            this.to_name = data.to_name;
            this.to_city = data.to_city;
            this.to_county = data.to_county;
            this.to_address = data.to_address;
            this.hub = data.hub;
            this.type = data.type;
            this.weight = data.weight;
            this.to_phone = data.to_phone;
            this.from_phone = data.from_phone;
            this.width = data.width;
            this.length = data.length;
            this.height = data.height;
            this.comments = data.comments;
            this.to_zipcode = data.to_zipcode;
            this.from_zipcode = data.from_zipcode;
            this.customer_reference = data.customer_reference;
            this.unload_delay_kpi = data.unload_delay_kpi ?? null;
            this.department_name = data.department_name ?? null;
            this.has_labels = data.has_labels ?? false;
            this.strongoShipmentParcels = [];
            if (data.strongoShipmentParcels !== undefined) {
                data.strongoShipmentParcels.forEach(strongoShipmentParcel => this.strongoShipmentParcels.push((new StrongoShipmentParcel(strongoShipmentParcel))));
            }
            this.company = data.company;
            this.transportOrder = null;
            if (data.transportOrder !== undefined && data.transportOrder !== null) {
                this.transportOrder = new StrongoTransportOrder(data.transportOrder);
            }
        }
    }
}

export type StrongoShipmentData = {
    id: number;
    company_id: number;
    client_code: string;
    no: string;
    from_time: Date | null;
    scheduled_pickup_date: Date | null;
    pickup_date: Date | null;
    to_time: Date | null;
    scheduled_delivery_date: Date | null;
    last_status_date: Date | null;
    status: string;
    substatus: string;
    color: string;
    text_color: string;
    description: string;
    from_name: string;
    from_city: string;
    from_county: string;
    from_address: string;
    to_name: string;
    to_city: string;
    to_county: string;
    to_address: string;
    hub: string;
    type: string;
    weight: number;
    to_phone: number;
    from_phone: number;
    width: number;
    length: number;
    height: number;
    comments: string;
    to_zipcode: string;
    from_zipcode: string;
    customer_reference: string;
    unload_delay_kpi: string | null;
    department_name: string | null;
    has_labels: boolean;
    strongoShipmentParcels: StrongoShipmentParcelData[] | undefined;
    company: Company | null;
    transportOrder: StrongoTransportOrderData | null;
}